import { toFixed } from '../../../../utils/utils'

/**
 * Lumpsum allocation percentages — label and value built together.
 * For gaps, call site picks `gapMode`: round(raw diff) vs round(each) then subtract.
 */

export type PlanPercentage = {
  value: number
  label: string
}

export type PlanPercentageGap = {
  rawDifference: number
  displayPercentage: PlanPercentage
}

export type PlanPercentageGapMode = 'roundRawDifference' | 'roundEachThenSubtract'

export type PlanAllocationRangeInput = {
  lowerLimit?: number | null
  upperLimit?: number | null
  absolute?: number | null
  lowerimit?: number | null
}

export function buildPlanPercentage({
  rawPercentage
}: {
  rawPercentage: number
}): PlanPercentage {
  const value = normalizePlanPercentage(rawPercentage)
  return { value, label: formatPercentageLabel(value) }
}

export function buildPlanPercentageGap({
  fromPercentage,
  toPercentage,
  gapMode
}: {
  fromPercentage: number
  toPercentage: number
  gapMode: PlanPercentageGapMode
}): PlanPercentageGap {
  const rawDifference = fromPercentage - toPercentage
  const displayValue =
    gapMode === 'roundRawDifference'
      ? normalizePlanPercentage(rawDifference)
      : normalizePlanPercentage(fromPercentage) - normalizePlanPercentage(toPercentage)

  return {
    rawDifference,
    displayPercentage: toPlanPercentage(displayValue)
  }
}

export function buildPlanAllocationRangeLabel({
  idealAllocation
}: {
  idealAllocation?: PlanAllocationRangeInput | null
}): string {
  const lowerLimit = idealAllocation?.lowerLimit ?? idealAllocation?.lowerimit ?? 0
  const upperLimit = idealAllocation?.upperLimit ?? 0
  if (lowerLimit > 0 || upperLimit > 0) {
    const lowerLabel = buildPlanPercentage({ rawPercentage: lowerLimit }).label
    const upperLabel = buildPlanPercentage({ rawPercentage: upperLimit }).label
    // Compare the rounded labels. 4.6 and 5.4 both read as 5%, so that is one percent.
    if (lowerLabel === upperLabel) {
      return upperLabel
    }
    return `${lowerLabel.slice(0, -1)}–${upperLabel}`
  }
  const absolute = idealAllocation?.absolute ?? 0
  if (absolute > 0) {
    return buildPlanPercentage({ rawPercentage: absolute }).label
  }
  return ''
}

/** Display-only — use buildPlanPercentage when the same value is executed. */
export function planPercentageLabel({ rawPercentage }: { rawPercentage: number }): string {
  return buildPlanPercentage({ rawPercentage }).label
}

/** 0 → 0; (-1, 1) excluding 0 → two decimals; |value| ≥ 1 → Math.round. */
export function normalizePlanPercentage(rawPercentage: number): number {
  const safeValue = typeof rawPercentage === 'number' && Number.isFinite(rawPercentage) ? rawPercentage : 0
  if (safeValue === 0) {
    return 0
  }
  if (safeValue > -1 && safeValue < 1) {
    return Number(toFixed(safeValue, 2))
  }
  return Math.round(safeValue)
}

function toPlanPercentage(normalizedValue: number): PlanPercentage {
  return { value: normalizedValue, label: formatPercentageLabel(normalizedValue) }
}

function formatPercentageLabel(normalizedValue: number): string {
  return `${normalizedValue}%`
}
